(() => {
  const SELECTOR = '[data-bsg-partner-signup]';

  function addressFrom(formData, prefix) {
    const value = (key) => String(formData.get(`${prefix}[${key}]`) || '').trim();

    return {
      countryCode: 'US',
      firstName: value('firstName'),
      lastName: value('lastName'),
      recipient: value('recipient'),
      address1: value('address1'),
      address2: value('address2'),
      city: value('city'),
      zoneCode: value('zoneCode').toUpperCase(),
      zip: value('zip'),
      phone: value('phone')
    };
  }

  class PartnerSignup {
    constructor(section) {
      if (section.dataset.initialized === 'true') return;

      this.section = section;
      this.form = section.querySelector('[data-signup-form]');
      this.sameAsBilling = section.querySelector('[data-same-as-billing]');
      this.shippingFields = section.querySelector('[data-shipping-fields]');
      this.error = section.querySelector('[data-form-error]');
      this.success = section.querySelector('[data-form-success]');
      this.submitButton = section.querySelector('[data-submit-button]');
      this.submitLabel = section.querySelector('[data-submit-label]');
      this.submitLoading = section.querySelector('[data-submit-loading]');
      this.startedAt = Date.now();

      if (!this.form || !this.sameAsBilling || !this.shippingFields) return;

      section.dataset.initialized = 'true';
      this.sameAsBilling.addEventListener('change', () => this.updateShipping());
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.updateShipping();
    }

    updateShipping() {
      const useBilling = this.sameAsBilling.checked;
      this.shippingFields.hidden = useBilling;
      this.shippingFields.querySelectorAll('input, select, textarea').forEach((field) => {
        field.disabled = useBilling;
      });
    }

    setLoading(loading) {
      this.form.setAttribute('aria-busy', String(loading));
      this.submitButton.disabled = loading;
      this.submitLabel.hidden = loading;
      this.submitLoading.hidden = !loading;
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.error.focus({ preventScroll: true });
      this.error.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
    }

    payload() {
      const formData = new FormData(this.form);
      const billing = addressFrom(formData, 'billing');
      const sameAsBilling = this.sameAsBilling.checked;

      return {
        action: 'partner_signup',
        companyName: String(formData.get('companyName') || '').trim(),
        email: String(formData.get('email') || '').trim().toLowerCase(),
        phone: String(formData.get('phone') || '').trim(),
        website: String(formData.get('website') || '').trim(),
        taxId: String(formData.get('taxId') || '').trim(),
        billing,
        shipping: sameAsBilling ? { ...billing } : addressFrom(formData, 'shipping'),
        sameAsBilling,
        websiteConfirmation: String(formData.get('websiteConfirmation') || ''),
        startedAt: this.startedAt,
        pageUri: window.location.href
      };
    }

    async submit(event) {
      event.preventDefault();
      this.clearError();

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      const endpoint = String(this.section.dataset.endpoint || '').trim();
      if (!endpoint) {
        this.showError('This form is not available right now. Please contact us for assistance.');
        return;
      }

      this.setLoading(true);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.payload())
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.ok !== true) {
          throw new Error(result.error || 'We could not submit your application. Please try again.');
        }

        this.form.hidden = true;
        this.success.hidden = false;
        this.success.focus({ preventScroll: true });
        this.success.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (error) {
        this.showError(
          error instanceof Error && error.message
            ? error.message
            : 'We could not submit your application. Please try again.'
        );
      } finally {
        this.setLoading(false);
      }
    }
  }

  function initialize(container = document) {
    if (container.matches?.(SELECTOR)) new PartnerSignup(container);
    container.querySelectorAll(SELECTOR).forEach((section) => new PartnerSignup(section));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
